import React, { useContext, useEffect, useRef, useState } from "react";
import {
    ResponsiveDateContainer,
    TimesheetCellDropdown,
    TimesheetCellHead,
    TimesheetCellHeadDayText,
    TimesheetCellHeadTotalText,
    TimesheetCellHeadVStack,
    TimesheetRowHead,
} from "features/timesheet/timesheet-grid/TimesheetGrid.styles";
import { ResponsiveErrorMessage, StyledErrorHStack } from "./Header.styles";
import { compareAsc, format } from "date-fns";
import { getLocalTimeZone, isToday, today } from "@internationalized/date";

import { DAY_HOURS_LIMIT } from "features/timesheet/timesheet-grid/TimesheetGrid";
import Icon from "shared/components/utils/Icon";
import SessionContext from "app/contexts/SessionContext";
import Text from "shared/components/data-display/text/Text";
import TimesheetGridContext from "features/timesheet/contexts/TimesheetGridContext";
import WeekNavigatorContext from "shared/contexts/WeekNavigatorContext";
import WeekNavigatorMain from "shared/components/date-controls/week-navigator/WeekNavigatorMain";
import moment from "moment";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";
import useTimeOffApi from "../time-off/querys/useTimeOff";
import { useTitles } from "features/allocations/api/useTitles";
import { useWeeksTimeEntriesApi } from "features/timesheet/timesheet-grid/useTimesheetGrid";

const Header = () => {
    const session = useContext(SessionContext);
    const { useWeeksTimeEntries } = useWeeksTimeEntriesApi();
    const isSmallScreen = useSmallBreakpoint();

    const {
        setIncompleteWeekStartDateList,
        fromDateToUpdateList,
        updateList,
        setUpdateList,
        setIsWeekChange,
        selectedTimesheetStartDate,
        setSelectedTimesheetStartDate,
    } = useContext(WeekNavigatorContext);

    const {
        projectHours,
        setWeekColumnDayHoursLimitExceed,
        setWeekDetails,
        setTotalOfWeekHours,
    } = useContext(TimesheetGridContext);

    const weekHoursRef = useRef([]);
    const [weekColumnTotal, setWeekColumnTotal] = useState([]);

    let fromDateVal = fromDateToUpdateList ?? selectedTimesheetStartDate;
    fromDateVal = fromDateVal ?? today(getLocalTimeZone());

    const requestData = {
        employeeId: session.legacyId,
        fromDate: `${fromDateVal.year}-${fromDateVal.month}-${fromDateVal.day}`,
    };
    const weeksData = useWeeksTimeEntries(requestData);

    const handleOnChange = (dateValue) => {
        setSelectedTimesheetStartDate(dateValue);
        setIsWeekChange(true);
    };

    const { useGetUserTitles } = useTitles();

    const titlesQueryResult = useGetUserTitles({ userId: session.legacyId });

    const { useGetApprovedTimeOffByDate } = useTimeOffApi();
    const approvedTimeOffs = useGetApprovedTimeOffByDate({
        from: `${fromDateVal.year - 1}-1-1`,
        until: `${fromDateVal.year + 1}-1-1`,
    });

    useEffect(() => {
        const len = projectHours?.length || 0;
        weekHoursRef.current = [0, 0, 0, 0, 0, 0, 0];

        for (let i = 0; i < len; i++) {
            for (let j = 0; j < 7; j++) {
                weekHoursRef.current[j] =
                    weekHoursRef.current[j] +
                    (projectHours[i]?.hours[j]?.timeSpan || 0);
            }
        }
        let total = 0;
        weekHoursRef.current.forEach((hour) => (total += hour));
        setTotalOfWeekHours(total);
        setWeekColumnTotal(weekHoursRef.current);

        if (weekHoursRef.current.some((d) => d > DAY_HOURS_LIMIT)) {
            setWeekColumnDayHoursLimitExceed(true);
        } else {
            setWeekColumnDayHoursLimitExceed(false);
        }
    }, [projectHours, setTotalOfWeekHours, setWeekColumnDayHoursLimitExceed]);

    useEffect(() => {
        if (!weeksData.isLoading) {
            setWeekDetails(weeksData?.data);
        }
    }, [weeksData, setWeekDetails]);

    useEffect(() => {
        if (!weeksData.isLoading && updateList) {
            setIncompleteWeekStartDateList(
                weeksData?.data?.filter(
                    (w) =>
                        !w.completed &&
                        compareAsc(new Date(w.date), new Date()) <= 0
                )
            );
            setUpdateList(false);
        }
    }, [weeksData, updateList, setIncompleteWeekStartDateList, setUpdateList]);

    if (weeksData.isError) {
        return <ResponsiveErrorMessage>Error</ResponsiveErrorMessage>;
    }

    let weekDates = [];
    if (selectedTimesheetStartDate) {
        for (let i = 0; i < 7; i++) {
            const weekDayDate = selectedTimesheetStartDate.add({ days: i });
            const dateFormat = new Date(
                weekDayDate.year,
                weekDayDate.month - 1,
                weekDayDate.day
            );

            weekDates.push({
                calendarDate: weekDayDate,
                dayName: format(dateFormat, "EEE"),
                monthName: format(dateFormat, "MMM"),
            });
        }
    }

    // Check if any day in the week has a double-digit date (10 or higher)
    // Only apply this logic on Small breakpoint for consistent vertical stacking
    const hasDoubleDigitDate =
        isSmallScreen && weekDates.some((date) => date.calendarDate.day >= 10);

    const getTimesheetRequiredDateRanges = () => {
        let dateRangesTimesheetRequired = [];

        titlesQueryResult?.data?.forEach((x) => {
            if (x.Title.TimeSheetRequired) {
                dateRangesTimesheetRequired.push({
                    startDate: moment(x.TitleStartDate)
                        .startOf("isoWeek")
                        .format("YYYY-MM-DD")
                        .toString(),
                    endDate: x.TitleEndDate
                        ? x.TitleEndDate
                        : moment(new Date()).format("YYYY-MM-DD"),
                });
            }
        });

        return dateRangesTimesheetRequired;
    };

    const getOnleaveDateRanges = () => {
        let dateRangesOnLeave = [];
        if (session.freelancer) {
            approvedTimeOffs?.data?.forEach((x) => {
                dateRangesOnLeave.push({
                    startDate: moment(x.firstDayOut)
                        .format("YYYY-MM-DD")
                        .toString(),
                    endDate: moment(x.lastDayOut)
                        .format("YYYY-MM-DD")
                        .toString(),
                    icon: "error",
                    label: "On Leave",
                });
            });
        }

        return dateRangesOnLeave;
    };

    return (
        <TimesheetRowHead>
            <TimesheetCellDropdown>
                <WeekNavigatorMain
                    label="Week Navigator"
                    defaultValue={today(getLocalTimeZone())}
                    onChange={handleOnChange}
                    dateOnChange={selectedTimesheetStartDate}
                    dateRangesWithWarning={getTimesheetRequiredDateRanges()}
                    dateRangesWithIcon={getOnleaveDateRanges()}
                />
            </TimesheetCellDropdown>
            {weekDates.map((data, index) => {
                const overLimitDisplay = isSmallScreen ? (
                    <TimesheetCellHeadTotalText
                        variant="Headline_4"
                        color="Orange_1"
                    >
                        {weekColumnTotal[index]}
                    </TimesheetCellHeadTotalText>
                ) : (
                    <StyledErrorHStack align="center" justify="space-between">
                        <Text variant="Headline_4" color="Orange_1">
                            {weekColumnTotal[index]}
                        </Text>
                        <Icon color="Orange_1" name="error" />
                    </StyledErrorHStack>
                );
                return (
                    <TimesheetCellHead key={data.calendarDate}>
                        <TimesheetCellHeadVStack>
                            <ResponsiveDateContainer
                                $forceVerticalStack={hasDoubleDigitDate}
                            >
                                <TimesheetCellHeadDayText
                                    variant="Headline_5"
                                    color="Black"
                                    current={isToday(data.calendarDate)}
                                >
                                    {data.dayName}
                                </TimesheetCellHeadDayText>
                                {hasDoubleDigitDate ? (
                                    <>
                                        <Text
                                            variant="Descriptive_2_1"
                                            color="Gray_2"
                                        >
                                            {data.monthName}
                                        </Text>
                                        <Text
                                            variant="Descriptive_2_1"
                                            color="Gray_2"
                                        >
                                            {data.calendarDate.day}
                                        </Text>
                                    </>
                                ) : (
                                    <Text
                                        variant="Descriptive_2_1"
                                        color="Gray_2"
                                    >
                                        {data.monthName} {data.calendarDate.day}
                                    </Text>
                                )}
                            </ResponsiveDateContainer>
                            {(data.dayName === "Sun" && !weekColumnTotal[6]) ||
                            (data.dayName === "Sat" && !weekColumnTotal[5]) ? (
                                <TimesheetCellHeadTotalText
                                    variant="Headline_4"
                                    color="Black"
                                >
                                    &#45;&#45;
                                </TimesheetCellHeadTotalText>
                            ) : (
                                <>
                                    {weekColumnTotal[index] >
                                    DAY_HOURS_LIMIT ? (
                                        overLimitDisplay
                                    ) : (
                                        <TimesheetCellHeadTotalText
                                            variant="Headline_4"
                                            color="Black"
                                        >
                                            {weekColumnTotal[index] ?? 0}
                                        </TimesheetCellHeadTotalText>
                                    )}
                                </>
                            )}
                        </TimesheetCellHeadVStack>
                    </TimesheetCellHead>
                );
            })}
        </TimesheetRowHead>
    );
};

export default Header;
